import React from 'react';
import { useState, useEffect } from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps, useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Button,
    ButtonProps,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Box,
    Container,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from '../hooks/auth/useAuth';
import AuthDialog from '../components/auth/AuthDialog';
import { loginPromptManager, LoginPromptType } from '../utils/auth/loginPromptManager';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Badge from '@mui/material/Badge';
import CartDrawer from '../components/quoter/cart/CartDrawer';
import { useCart } from '../utils/cart/CartContext';
import { getEnvironmentConfig, isProduction } from '../utils/environment';
import { systemApi } from '../services/systemApi';
import { MARITIME } from '../constants/colors';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.customColors?.appBarBackground || MARITIME.OFF_WHITE,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', // Subtle shadow for white header
    color: theme.palette.primary.contrastText,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: 0,
    paddingTop: theme.spacing(2.5),    // Add vertical padding
    paddingBottom: theme.spacing(2.5), // Add vertical padding
}));

const NavItems = styled(Stack)(({ theme }) => ({
    alignItems: 'center',
    marginLeft: theme.spacing(3),  // Reduced from 4 to bring closer to logo
    [theme.breakpoints.down('lg')]: {  // Changed from 'md' to 'lg' - switch to hamburger sooner
        display: 'none',
    },
}));

const MobileNavItems = styled(Box)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.down('lg')]: {  // Changed from 'md' to 'lg' - show hamburger sooner
        display: 'flex',
        alignItems: 'center',
    },
}));

const Logo = styled('img')(({ theme }) => ({
    height: '75px',
    [theme.breakpoints.down('lg')]: {  // Changed from 'md' to 'lg' for consistency
        height: '40px',
    },
}));

const OrangeButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.orange.main,
    color: theme.palette.getContrastText(theme.palette.orange.main),
    '&:hover': {
        backgroundColor: theme.palette.orange.dark,
    },
}));

const AdminButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'hasAlert',
})<{ hasAlert?: boolean }>(({ theme, hasAlert }) => ({
    backgroundColor: hasAlert ? '#f44336' : theme.palette.secondary.main,
    color: hasAlert ? '#ffffff' : theme.palette.getContrastText(theme.palette.secondary.main),
    '&:hover': {
        backgroundColor: hasAlert ? '#d32f2f' : theme.palette.secondary.dark,
    },
}));

const MobileMenu = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
        width: 'calc(100% - 10px)',
        maxWidth: 'calc(100% - 10px)',
        marginTop: '75px',
        marginLeft: '10px',
        borderLeft: '4px solid',
        borderColor: theme.palette.orange.main,
        '& .MuiMenuItem-root': {
            padding: '12px 24px',
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
            }
        }
    },
}));

const EnvironmentIndicator = styled(Box)<{ color: string }>(({ theme, color }) => ({
    backgroundColor: color,
    color: '#ffffff',
    textAlign: 'center',
    padding: '4px 0',
    fontSize: '12px',
    fontWeight: 'bold',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar + 1,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '@media print': {
        display: 'none',
    },
}));

type OrangeButtonLinkProps = {
    to: string;
    children: React.ReactNode;
} & Omit<ButtonProps, 'href'>;

const OrangeButtonLink: React.FC<OrangeButtonLinkProps> = ({ to, children, ...props }) => {
    const RouterLinkComponent = React.useMemo(
        () =>
            React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'>>((linkProps, ref) => (
                <RouterLink ref={ref} to={to} {...linkProps} />
            )),
        [to]
    );

    return (
        <OrangeButton component={RouterLinkComponent} {...props}>
            {children}
        </OrangeButton>
    );
};

const Header: React.FC = () => {
    const envConfig = getEnvironmentConfig();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [sailsAnchorEl, setSailsAnchorEl] = useState<null | HTMLElement>(null);
    const [measurementAnchorEl, setMeasurementAnchorEl] = useState<null | HTMLElement>(null);
    const [adminAnchorEl, setAdminAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileAnchorEl, setMobileAnchorEl] = useState<null | HTMLElement>(null);
    const location = useLocation();
    const { user, isAuthenticated, isAnonymous, logout, refreshUserInfo } = useAuth();
    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const navigate = useNavigate();
    const [showProfileDialog, setShowProfileDialog] = useState(false);
    const [authPromptType, setAuthPromptType] = useState<LoginPromptType>(LoginPromptType.SIGN_IN);
    const [authPrefillEmail, setAuthPrefillEmail] = useState('');
    const [authPrefillFirstName, setAuthPrefillFirstName] = useState('');
    const [authPrefillLastName, setAuthPrefillLastName] = useState('');
    const { isCartOpen, openCart, closeCart, cartCount } = useCart();
    const [hasMismatch, setHasMismatch] = useState(false);
    const [discountBanner, setDiscountBanner] = useState<{ DiscountHeader: string; DiscountDescripton: string } | null>(null);

    // Listen for openAuthDialog custom events from other components
    useEffect(() => {
        const handleOpenAuthEvent = (event: CustomEvent<{
            mode?: 'login' | 'register';
            email?: string;
            firstName?: string;
            lastName?: string;
        }>) => {
            const { mode, email, firstName, lastName } = event.detail || {};
            if (mode === 'register') {
                setAuthPromptType(LoginPromptType.CREATE_ACCOUNT);
            } else {
                setAuthPromptType(LoginPromptType.SIGN_IN);
            }
            // Set prefill values if provided
            setAuthPrefillEmail(email || '');
            setAuthPrefillFirstName(firstName || '');
            setAuthPrefillLastName(lastName || '');
            setShowAuthDialog(true);
        };

        window.addEventListener('openAuthDialog', handleOpenAuthEvent as EventListener);
        return () => {
            window.removeEventListener('openAuthDialog', handleOpenAuthEvent as EventListener);
        };
    }, []);

    // Check if user is admin (userTypeId >= 1000)
    const isAdmin = isAuthenticated && !isAnonymous && (user.userTypeId ?? 0) >= 1000;

    // Listen for mismatch check events and check on mount for admins
    useEffect(() => {
        const shouldCheck = !isProduction() || isAdmin;
        
        if (!shouldCheck) {
            return;
        }

        const checkMismatch = async () => {
            const result = await systemApi.checkQuoteMismatchExists(
                user?.userId,
                user?.userTypeId
            );
            setHasMismatch(result);
        };

        // Check on mount
        checkMismatch();

        // Listen for custom event
        const handleMismatchCheckEvent = () => {
            checkMismatch();
        };

        window.addEventListener('quoteMismatchCheck', handleMismatchCheckEvent);

        return () => {
            window.removeEventListener('quoteMismatchCheck', handleMismatchCheckEvent);
        };
    }, [isAdmin, user?.userId, user?.userTypeId]);

    // Fetch discount banner on mount
    useEffect(() => {
        const fetchDiscountBanner = async () => {
            const banner = await systemApi.getDiscountBanner();
            setDiscountBanner(banner);
        };
        fetchDiscountBanner();
    }, []);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSailsMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setSailsAnchorEl(event.currentTarget);
    };

    const handleSailsMenuClose = () => {
        setSailsAnchorEl(null);
    };

    const handleMeasurementMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMeasurementAnchorEl(event.currentTarget);
    };

    const handleMeasurementMenuClose = () => {
        setMeasurementAnchorEl(null);
    };

    const handleAdminMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAdminAnchorEl(event.currentTarget);
    };

    const handleAdminMenuClose = () => {
        setAdminAnchorEl(null);
    };

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMobileAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileAnchorEl(null);
        setSailsAnchorEl(null);
        setMeasurementAnchorEl(null);
        setAdminAnchorEl(null);
        setAnchorEl(null);
    };

    // Authentication handlers
    const handleOpenAuthDialog = () => {
        const newPromptType = loginPromptManager.getAppropriatePromptType(
            isAnonymous,
            isAuthenticated
        );
        setAuthPromptType(newPromptType);
        setShowAuthDialog(true);
    };

    const handleCloseAuthDialog = () => {
        setShowAuthDialog(false);
        // Clear prefill values when dialog closes
        setAuthPrefillEmail('');
        setAuthPrefillFirstName('');
        setAuthPrefillLastName('');
    };

    const handleAuthSuccess = () => {
        // Force refresh of user info to update the header display
        refreshUserInfo();

        // No reload needed - the useAuth hook should handle state updates
        console.log('Authentication successful - user info refreshed');
    };

    // Styled component for user info
    const UserInfoContainer = styled(Box)(({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginRight: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    }));

    const UserInfoText = styled(Typography)(({ theme }) => ({
        fontSize: '0.75rem',
        lineHeight: 1.2,
        color: theme.palette.primary.contrastText,
    }));

    const handleProfileClick = () => {
        handleMenuClose();
        // Store the current path as the referrer
        sessionStorage.setItem('profileReferrer', location.pathname + location.search);
        // Navigate to profile
        navigate('/profile');
    };

    // User menu component
    const UserMenuSection: React.FC = () => {
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const { user, isAuthenticated, isAnonymous, logout, refreshUserInfo } = useAuth();

        const displayName = isAuthenticated
            ? (isAnonymous ? 'Guest' : (user.userName || 'My Account'))
            : 'Login / Register';

        const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        const handleLogoutClick = () => {
            handleMenuClose();
            logout();
            navigate('/');
        };

        const handleCreateAccountClick = () => {
            handleMenuClose();
            setShowProfileDialog(true);
        };

        // Authentication handlers
        const handleOpenAuthDialog = () => {
            // Determine the appropriate prompt type
            const promptType = isAnonymous ? LoginPromptType.CONVERT_ANONYMOUS : LoginPromptType.SIGN_IN;
            setShowAuthDialog(true);
        };

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 2 }}>
                {isAuthenticated ? (
                    <>
                        <Button
                            color="inherit"
                            onClick={handleMenuOpen}
                            sx={{ fontSize: '0.8125rem', color: 'inherit' }}
                        >
                            {user.userName_DisplayMenu || (user.isAnonymous ? 'Guest' : (user.userName || 'My Account'))}
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem component={RouterLink} to="/dashboard" onClick={handleMenuClose}>My Dashboard</MenuItem>
                            {isAnonymous && <MenuItem onClick={handleCreateAccountClick}>Create Account</MenuItem>}
                            {isAnonymous && <MenuItem onClick={handleOpenAuthDialog}>Login</MenuItem>}
                            {!isAnonymous && <MenuItem onClick={handleProfileClick}>My Profile</MenuItem>}
                            {!isAnonymous && <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>}
                        </Menu>
                    </>
                ) : (
                    <Button
                        color="inherit"
                        onClick={handleOpenAuthDialog}
                        sx={{ fontSize: '0.8125rem', color: 'inherit' }}
                    >
                        {displayName}
                    </Button>
                )}
            </Box>
        );
    };

    // Calculate header offset based on indicators shown
    const envIndicatorHeight = envConfig.showIndicator ? 24 : 0;
    const discountBannerHeight = discountBanner ? 24 : 0;
    const totalIndicatorHeight = envIndicatorHeight + discountBannerHeight;

    return (
        <>
            {/* Environment Indicator */}
            {envConfig.showIndicator && (
                <EnvironmentIndicator
                    color={hasMismatch ? envConfig.alertColor : envConfig.indicatorColor}
                    className="environment-indicator no-print"
                >
                    {envConfig.displayName} ENVIRONMENT
                </EnvironmentIndicator>
            )}

            {/* Discount Banner */}
            {discountBanner && (
                <EnvironmentIndicator
                    color="#2196f3"
                    className="discount-banner no-print"
                    sx={{ top: envIndicatorHeight }}
                >
                    {discountBanner.DiscountHeader}
                </EnvironmentIndicator>
            )}

            <StyledAppBar position="fixed" sx={{ top: totalIndicatorHeight, boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
                <Container maxWidth={false} sx={{ maxWidth: '1800px' }}>
                    <StyledToolbar>
                        <RouterLink to="/">
                            <Logo src="/images/point-sailsLogo.svg" alt="Point Sails" />
                        </RouterLink>

                        <NavItems direction="row" spacing={2.4}>
                            <OrangeButtonLink to="/quoter" variant="contained" sx={{ marginRight: 3, textAlign: 'center' }}>
                                Online Quote
                            </OrangeButtonLink>
                            <Button
                                color="inherit"
                                aria-controls="sails-menu"
                                aria-haspopup="true"
                                onClick={handleSailsMenuOpen}
                                sx={{ fontSize: '0.8125rem', color: 'inherit' }}
                            >
                                Sails
                            </Button>
                            <Menu
                                id="sails-menu"
                                anchorEl={sailsAnchorEl}
                                keepMounted
                                open={Boolean(sailsAnchorEl)}
                                onClose={handleSailsMenuClose}
                            >
                                <MenuItem onClick={handleSailsMenuClose} component={RouterLink} to="/cruising-sails">
                                    Cruising Sails
                                </MenuItem>
                                <MenuItem onClick={handleSailsMenuClose} component={RouterLink} to="/performance-sails">
                                    Performance Sails
                                </MenuItem>
                                <MenuItem onClick={handleSailsMenuClose} component={RouterLink} to="/downwind-sails">
                                    Downwind Sails
                                </MenuItem>
                                <MenuItem onClick={handleSailsMenuClose} component={RouterLink} to="/code-zero-sails">
                                    Code Zero
                                </MenuItem>
                            </Menu>

                            <Button
                                color="inherit"
                                aria-controls="measurement-menu"
                                aria-haspopup="true"
                                onClick={handleMeasurementMenuOpen}
                                sx={{ fontSize: '0.8125rem', color: 'inherit' }}
                            >
                                Measurement Forms
                            </Button>
                            <Menu
                                id="measurement-menu"
                                anchorEl={measurementAnchorEl}
                                keepMounted
                                open={Boolean(measurementAnchorEl)}
                                onClose={handleMeasurementMenuClose}
                            >
                                <MenuItem component={RouterLink} to="/measurement/mainsail" onClick={handleMeasurementMenuClose}>
                                    Mainsail Measurement Form
                                </MenuItem>
                                <MenuItem component={RouterLink} to="/measurement/imf-mainsail" onClick={handleMeasurementMenuClose}>
                                    IMF Mainsail Measurement Form
                                </MenuItem>
                                <MenuItem component={RouterLink} to="/measurement/furling-headsail" onClick={handleMeasurementMenuClose}>
                                    Furling Headsail Measurement Form
                                </MenuItem>
                                <MenuItem component={RouterLink} to="/measurement/nonfurling-headsail" onClick={handleMeasurementMenuClose}>
                                    Non Furling Headsail Measurement Form
                                </MenuItem>
                            </Menu>


                            <Button component={RouterLink} to="/about" color="inherit" sx={{ fontSize: '0.8125rem', color: 'inherit' }}>
                                About
                            </Button>
                            <Button
                                color="inherit"
                                aria-controls="more-menu"
                                aria-haspopup="true"
                                onClick={handleMenuOpen}
                                sx={{ fontSize: '0.8125rem', color: 'inherit' }}
                            >
                                More
                            </Button>
                            <Menu
                                id="more-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                            >
                                {/*<MenuItem onClick={handleMenuClose} component={RouterLink} to="/blog">*/}
                                {/*    Blog*/}
                                {/*</MenuItem>*/}
                                {/*<MenuItem onClick={handleMenuClose} component={RouterLink} to="/FeaturedProjectsPage">*/}
                                {/*    Featured Projects*/}
                                {/*</MenuItem>*/}
                                <MenuItem onClick={handleMenuClose} component={RouterLink} to="/sail-cloth">
                                    Sail Cloth
                                </MenuItem>
                                <MenuItem onClick={handleMenuClose} component={RouterLink} to="/sail-ordering-process">
                                    Ordering Process
                                </MenuItem>
                                <MenuItem onClick={handleMenuClose} component={RouterLink} to="/warranty">
                                    Warranty
                                </MenuItem>
                                {/*<MenuItem onClick={handleMenuClose} component={RouterLink} to="/learning-resources">*/}
                                {/*    Learning Resources*/}
                                {/*</MenuItem>*/}
                                <MenuItem onClick={handleMenuClose} component={RouterLink} to="/privacy-policy">
                                    Privacy Policy
                                </MenuItem>
                                <MenuItem onClick={handleMenuClose} component={RouterLink} to="/contact">
                                    Contact Us
                                </MenuItem>
                                <MenuItem onClick={handleMenuClose} component="a" href="https://www.whirlwindsails.com/" target="_blank" rel="noopener noreferrer">
                                    Whirlwind Sails
                                </MenuItem>
                            </Menu>

                            {/* ADMIN MENU - Only show if userTypeId >= 1000 */}
                            {isAdmin && (
                                <>
                                    <AdminButton
                                        variant="contained"
                                        aria-controls="admin-menu"
                                        aria-haspopup="true"
                                        onClick={handleAdminMenuOpen}
                                        startIcon={<AdminPanelSettingsIcon />}
                                        hasAlert={isProduction() && hasMismatch}
                                    >
                                        Admin
                                    </AdminButton>
                                    <Menu
                                        id="admin-menu"
                                        anchorEl={adminAnchorEl}
                                        keepMounted
                                        open={Boolean(adminAnchorEl)}
                                        onClose={handleAdminMenuClose}
                                    >
                                        <MenuItem onClick={handleAdminMenuClose} component={RouterLink} to="/admin/audit">
                                            Audit
                                        </MenuItem>
                                        <MenuItem onClick={handleAdminMenuClose} component={RouterLink} to="/admin/reset-password">
                                            Password Reset
                                        </MenuItem>
                                        <MenuItem onClick={handleAdminMenuClose} component={RouterLink} to="/admin/pre-approved-resellers">
                                            Pre-Approved Reseller Add
                                        </MenuItem>
                                        <MenuItem onClick={handleAdminMenuClose} component={RouterLink} to="/admin/discount-programs">
                                            Discount Programs
                                        </MenuItem>
                                        <MenuItem onClick={handleAdminMenuClose} component={RouterLink} to="/admin/cloth-config">
                                            Cloth Config
                                        </MenuItem>
                                        <MenuItem onClick={handleAdminMenuClose} component={RouterLink} to="/admin/dashboard">
                                            System Dashboard
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}

                            <UserMenuSection />
                            <IconButton
                                color="inherit"
                                onClick={openCart}
                                aria-label="Shopping cart"
                                sx={{ marginLeft: 2, color: 'inherit' }}
                            >
                                <Badge badgeContent={cartCount} color="secondary" overlap="circular">
                                    <ShoppingCartIcon />
                                </Badge>
                            </IconButton>
                        </NavItems>

                        <MobileNavItems>
                            {isAuthenticated && !isAnonymous && (
                                <PersonIcon sx={{ mr: 1, fontSize: 20, color: 'inherit' }} />
                            )}
                            <IconButton
                                color="inherit"
                                onClick={openCart}
                                aria-label="Shopping cart"
                                sx={{ color: 'inherit' }}
                            >
                                <Badge badgeContent={cartCount} color="secondary" overlap="circular">
                                    <ShoppingCartIcon />
                                </Badge>
                            </IconButton>
                            <IconButton
                                color="inherit"
                                onClick={handleMobileMenuOpen}
                                aria-label="menu"
                                sx={{ color: 'inherit' }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </MobileNavItems>

                        <MobileMenu
                            anchorEl={mobileAnchorEl}
                            open={Boolean(mobileAnchorEl)}
                            onClose={handleMobileMenuClose}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <Stack spacing={0}>
                                <MenuItem component={RouterLink} to="/quoter" onClick={handleMobileMenuClose}>
                                    Online Quote
                                </MenuItem>
                                <MenuItem onClick={(e) => handleSailsMenuOpen(e as any)}>
                                    Sails
                                </MenuItem>
                                <Menu
                                    anchorEl={sailsAnchorEl}
                                    open={Boolean(sailsAnchorEl)}
                                    onClose={handleSailsMenuClose}
                                >
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/cruising-sails">
                                        Cruising Sails
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/performance-sails">
                                        Performance Sails
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/downwind-sails">
                                        Downwind Sails
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/code-zero-sails">
                                        Code Zero
                                    </MenuItem>
                                </Menu>

                                <MenuItem onClick={(e) => handleMeasurementMenuOpen(e as any)}>
                                    Measurement Forms
                                </MenuItem>
                                <Menu
                                    anchorEl={measurementAnchorEl}
                                    open={Boolean(measurementAnchorEl)}
                                    onClose={handleMeasurementMenuClose}
                                >
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/measurement/mainsail">
                                        Mainsail Measurement Form
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/measurement/imf-mainsail">
                                        IMF Mainsail Measurement Form
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/measurement/furling-headsail">
                                        Furling Headsail Measurement Form
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/measurement/nonfurling-headsail">
                                        Non Furling Headsail Measurement Form
                                    </MenuItem>
                                </Menu>


                                <MenuItem component={RouterLink} to="/about" onClick={handleMobileMenuClose}>
                                    About
                                </MenuItem>
                                <MenuItem onClick={(e) => handleMenuOpen(e as any)}>
                                    More
                                </MenuItem>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    {/*<MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/blog">*/}
                                    {/*    Blog*/}
                                    {/*</MenuItem>*/}
                                    {/*<MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/FeaturedProjectsPage">*/}
                                    {/*    Featured Projects*/}
                                    {/*</MenuItem>*/}
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/sail-cloth">
                                        Sail Cloth
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/sail-ordering-process">
                                        Ordering Process
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/warranty">
                                        Warranty
                                    </MenuItem>
                                    {/*<MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/learning-resources">*/}
                                    {/*    Learning Resources*/}
                                    {/*</MenuItem>*/}
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/privacy-policy">
                                        Privacy Policy
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/contact">
                                        Contact Us
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component="a" href="https://www.whirlwindsails.com/" target="_blank" rel="noopener noreferrer">
                                        Whirlwind Sails
                                    </MenuItem>
                                </Menu>

                                {/* ADMIN MENU ITEM FOR MOBILE */}
                                {isAdmin && (
                                    <MenuItem onClick={(e) => handleAdminMenuOpen(e as any)}>
                                        <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                                        Admin
                                    </MenuItem>
                                )}
                                <Menu
                                    anchorEl={adminAnchorEl}
                                    open={Boolean(adminAnchorEl)}
                                    onClose={handleAdminMenuClose}
                                >
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/admin/audit">
                                        Audit
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/admin/reset-password">
                                        Password Reset
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/admin/pre-approved-resellers">
                                        Pre-Approved Reseller Add
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/admin/discount-programs">
                                        Discount Programs
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/admin/cloth-config">
                                        Cloth Config
                                    </MenuItem>
                                    <MenuItem onClick={handleMobileMenuClose} component={RouterLink} to="/admin/dashboard">
                                        System Dashboard
                                    </MenuItem>
                                </Menu>

                                <MenuItem onClick={() => {
                                    if (isAuthenticated) {
                                        if (isAnonymous) {
                                            handleOpenAuthDialog(); // Convert anonymous account
                                        } else {
                                            handleProfileClick(); // Go to profile for authenticated users
                                        }
                                    } else {
                                        handleOpenAuthDialog(); // Sign in for unauthenticated users
                                    }
                                    handleMobileMenuClose();
                                }}>
                                    {isAuthenticated ? (isAnonymous ? "Complete Account" : "My Account") : "Sign In"}
                                </MenuItem>
                                <MenuItem onClick={() => { openCart(); handleMobileMenuClose(); }}>
                                    <Badge badgeContent={cartCount} color="secondary" sx={{ mr: 1 }}>
                                        <ShoppingCartIcon />
                                    </Badge>
                                    Cart
                                </MenuItem>
                            </Stack>
                        </MobileMenu>
                    </StyledToolbar>
                </Container>
            </StyledAppBar>
            <Box sx={{ height: 115 + totalIndicatorHeight }} /> {/* Spacer for fixed header + padding + indicators */}

            {/* Authentication Dialog */}
            <AuthDialog
                open={showAuthDialog}
                onClose={handleCloseAuthDialog}
                onSuccess={handleAuthSuccess}
                defaultPromptType={authPromptType}
                prefillEmail={authPrefillEmail}
                prefillFirstName={authPrefillFirstName}
                prefillLastName={authPrefillLastName}
            />
            <CartDrawer open={isCartOpen} onClose={closeCart} />
        </>
    );
};

export default Header;